var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc xAxis
 */
import { pixelOptimize } from '@byted/light-render/lib/utils/line1px';
import CanvasPath from '@byted/light-render/lib/geometry/Path2D';
import * as lodash from '../../utils/lodash';
import { Group, Line, Rect, Path, createRef } from '../../element';
import { createRichText } from '../../rich';
import Axis from './axis';
import { LinearScale, OrdinalScale, LogScale } from '../../scale';
import { axisType } from '../../interface/axis';
import { degToRad, inRange } from '../../utils/math';
import normalizeToArray from '../../utils/normalizeToArray';
import { eventGraph } from '../../action';
import measureText from '../../utils/measureText';
import { createSymbol, getSymbolShapeFromCenter } from '../../symbol/index';
import { renderMarkArea } from './markArea';
import { isArray } from '../../utils/lodash';
/**
 * default
 */
var YAxis = /** @class */ (function (_super) {
    __extends(YAxis, _super);
    function YAxis() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'yAxis';
        _this.shortType = 'y';
        _this.crossLine = new Line({ key: Math.random(), display: false, pointerEvents: 'none' });
        _this.crossShadow = new Rect({
            key: Math.random(),
            display: false,
            pointerEvents: 'none',
        });
        return _this;
    }
    YAxis.prototype.getInitialState = function () {
        return {
            min: 0,
            max: 0,
            top: 0,
            bottom: 0,
            crossAt: 0,
            labelSizeList: [],
        };
    };
    YAxis.prototype.beforeRender = function () {
        var _a = this.parentModel.getState(), top = _a.top, bottom = _a.bottom;
        var crossAt = this.getCrossPosition();
        this.setState({
            top: top,
            bottom: bottom,
            crossAt: crossAt,
        });
        this.initScale();
    };
    YAxis.prototype.initScale = function () {
        var _a = this.getExtreme(), min = _a[0], max = _a[1];
        this.setState({ min: min, max: max });
        var _b = this.option, type = _b.type, nice = _b.nice, logBase = _b.logBase, ticks = _b.ticks, inverse = _b.inverse, tickCount = _b.tickCount, exactTickCount = _b.exactTickCount, interval = _b.interval, minInterval = _b.minInterval, maxInterval = _b.maxInterval, padding = _b.padding, allowDecimals = _b.allowDecimals, breaks = _b.breaks;
        var _c = this.parentModel.getState(), top = _c.top, bottom = _c.bottom;
        var start = inverse ? top : bottom;
        var end = inverse ? bottom : top;
        if (type === axisType.VALUE) {
            var scale = new LinearScale(min, max, start, end);
            nice &&
                scale.niceTicks(tickCount, {
                    interval: interval,
                    ticks: ticks,
                    minInterval: minInterval,
                    maxInterval: maxInterval,
                    exactTickCount: exactTickCount,
                    allowDecimals: allowDecimals,
                });
            breaks && breaks.length && scale.setBreaks(breaks);
            this.scale = scale;
        }
        else if (type === axisType.CATEGORY) {
            var categoies = this.getCategories();
            var gapValue = padding === true ? 0.5 : 0;
            var scale = new OrdinalScale(categoies, min - gapValue, max + gapValue, start, end);
            this.scale = scale;
        }
        else if (type === axisType.LOG) {
            var scale = new LogScale(min, max, start, end, logBase);
            nice &&
                scale.niceTicks(tickCount, {
                    interval: interval,
                    ticks: ticks,
                    minInterval: minInterval,
                    maxInterval: maxInterval,
                    exactTickCount: exactTickCount,
                    allowDecimals: allowDecimals,
                });
            this.scale = scale;
        }
        this.scale.setFormatter(this.option.axisLabel.formatter);
    };
    YAxis.prototype.invertPoint = function (x, y) {
        return this.scale.invert(y);
    };
    YAxis.prototype.getCrossPosition = function () {
        var _a = this.parentModel.getState(), left = _a.left, right = _a.right;
        return this.option.opposite ? right : left;
    };
    YAxis.prototype.getLabelAreaSize = function () {
        var _this = this;
        var _a = this.option.axisLabel, show = _a.show, inside = _a.inside, margin = _a.margin, rotate = _a.rotate;
        var side = this.option.opposite ? 'right' : 'left';
        var ticks = this.scale.getTicks();
        var labelSize = 0;
        var titleSize = 0;
        var isIgnoreLabelSize = !this.option.show || !show || inside || ticks.length === 0;
        var hasTitle = this.option.title.show && this.option.title.text && this.option.title.align === 'middle';
        if (!isIgnoreLabelSize) {
            var labels = this.pickTicks(ticks).map(function (tick) { return _this.scale.getLabel(tick); });
            var labelSizeList = labels.map(function (label) { return measureText(label, _this.option.axisLabel); });
            labelSize = lodash.max(labelSizeList.map(function (itemSize) {
                var theta = degToRad(rotate);
                return itemSize.width * Math.cos(theta) + itemSize.height * Math.sin(theta) + margin;
            }));
            this.setState({ labelSizeList: labelSizeList });
        }
        if (hasTitle) {
            titleSize = this.option.title.margin + this.option.title.textStyle.fontSize;
        }
        return {
            side: side,
            size: labelSize + titleSize,
        };
    };
    YAxis.prototype.showCrosshair = function (x, y, type) {
        var _a = this.parentModel.getState(), left = _a.left, right = _a.right;
        var crossAt = this.state.crossAt;
        var useShadow = (this.shoudUseShadowPointer() && type !== 'line') || type === 'shadow';
        if (this.isCategroy()) {
            var index_1 = Math.round(this.scale.invert(y));
            var shadowTop = this.scale.scale(index_1 - 0.5);
            var shadowBottom = this.scale.scale(index_1 + 0.5);
            var centerY = Math.floor(this.scale.scale(index_1)) + 0.5;
            var hasMarkLine = this.option.markLines.some(function (mark) { return mark.value === index_1; });
            this.showCrossLabel(crossAt, this.scale.scale(index_1), this.getCategories()[index_1]);
            if (type === 'label') {
                return;
            }
            this.crossLine.setAttr(__assign({ display: !useShadow && !hasMarkLine, x1: left, x2: right, y1: centerY, y2: centerY }, this.getCrossLineStyle()));
            this.crossShadow.setAttr(__assign({ display: useShadow, x: Math.floor(left) + 0.5, y: Math.min(shadowTop, shadowBottom), width: right - left, height: Math.abs(shadowTop - shadowBottom) }, this.getCrossShadowStyle()));
        }
        else {
            var labelValue = this.scale.invert(y);
            if (!isNaN(labelValue)) {
                this.showCrossLabel(crossAt, y, this.scale.invert(y));
            }
            if (type === 'label') {
                return;
            }
            this.crossShadow.setAttr({
                display: false,
            });
            var yPosition = Math.floor(y) + 0.5;
            if (!useShadow) {
                this.crossLine.setAttr(__assign({ display: true, x1: left, x2: right, y1: yPosition, y2: yPosition }, this.getCrossLineStyle()));
            }
            else {
                this.crossLine.hide();
            }
        }
    };
    YAxis.prototype.renderAxisLine = function () {
        var _this = this;
        var _a = this.state, top = _a.top, bottom = _a.bottom, crossAt = _a.crossAt;
        var offset = this.option.offset;
        var x = Math.floor(crossAt) + 0.5;
        return new Line(__assign(__assign({ x1: x + offset, y1: top, x2: x + offset, y2: bottom }, this.getLineStyle(this.option.axisLine)), { onEvent: function (event) { return _this.handleUIEvent(event, eventGraph.AXIS_LINE, {}); } }));
    };
    YAxis.prototype.renderBrokenAxisLine = function () {
        var scale = this.scale;
        var brokenScales = scale.getBrokenScales();
        var brokenAxisLine = new Path(__assign(__assign({}, this.getLineStyle(this.option.axisLine)), { pointerEvents: 'none' }));
        var path = new CanvasPath();
        var crossAt = pixelOptimize(this.state.crossAt);
        var gap = this.option.inverse ? -10 : 10;
        for (var i = 0; i < brokenScales.length; i++) {
            var _a = brokenScales[i], start = _a.start, end = _a.end;
            path.moveTo(crossAt, i === 0 ? start : start - gap / 2);
            path.lineTo(crossAt, i === brokenScales.length - 1 ? end : end + gap / 2);
        }
        brokenAxisLine.setAttr('pathData', path);
        return brokenAxisLine;
    };
    YAxis.prototype.renderAlternateColor = function () {
        var ticks = this.getIntervalTicks();
        var ignoreColor = 'rgba(0, 0, 0, 0)';
        var colors = normalizeToArray(this.option.alternateColor);
        if (colors.length === 1) {
            colors.unshift(ignoreColor);
        }
        var group = new Group({
            key: this.getKey('axis-alternateColor'),
        });
        var _a = this.parentModel.getState(), left = _a.left, right = _a.right;
        if (ticks.length <= 1 || this.getAxisSeries().length === 0) {
            return group;
        }
        for (var i = 0; i < (this.isCategroy() ? ticks.length : ticks.length - 1); i++) {
            var color = colors[i % colors.length];
            if (color === ignoreColor) {
                continue;
            }
            var isLast = i === ticks.length - 1;
            var current = ticks[i];
            var next = ticks[i + 1];
            var start = this.isCategroy() ? this.scale.scale(current - 0.5) : this.scale.scale(current);
            var end = this.isCategroy()
                ? isLast
                    ? this.scale.end
                    : this.scale.scale(next - 0.5)
                : this.scale.scale(next);
            var y = Math.floor(Math.min(start, end)) + 0.5;
            var height = Math.abs(start - end);
            group.add(new Rect({
                x: left,
                y: lodash.clamp(y, this.state.top, this.state.bottom),
                width: right - left,
                height: height,
                fill: color,
            }));
        }
        return group;
    };
    YAxis.prototype.renderTickLines = function () {
        var _this = this;
        var crossAt = this.state.crossAt;
        var _a = this.option, tickLine = _a.tickLine, opposite = _a.opposite, offset = _a.offset;
        var tickGroup = new Group(__assign({ key: this.getKey('axis-tick') }, this.getLineStyle(this.option.tickLine)));
        var ticks = this.getIntervalTicks();
        var inside = tickLine.inside, length = tickLine.length;
        ticks.forEach(function (tick) {
            var y = _this.scale.scale(tick);
            var y1 = y;
            var y2 = y;
            var x1 = Math.floor(crossAt) + 0.5;
            var x2 = !opposite
                ? inside
                    ? x1 + length
                    : x1 - length
                : inside
                    ? x1 - length
                    : x1 + length;
            tickGroup.add(new Line({
                x1: x1 + offset,
                y1: y1,
                x2: x2 + offset,
                y2: y2,
                onEvent: function (event) { return _this.handleUIEvent(event, eventGraph.AXIS_TICKS, {}); },
            }));
        });
        return tickGroup;
    };
    YAxis.prototype.renderGridLines = function () {
        var _this = this;
        var _a = this.parentModel.getState(), top = _a.top, bottom = _a.bottom, left = _a.left, right = _a.right;
        var _b = this.option, padding = _b.padding, gridLine = _b.gridLine;
        var gridGroup = new Group(__assign({ key: this.getKey('axis-gridline') }, this.getLineStyle(gridLine)));
        var isCategroyAxis = this.isCategroy();
        var ticks = this.getIntervalTicks();
        if (this.isCategroy() && ticks.length > 0) {
            ticks.unshift(this.scale.min - 0.5);
            ticks.push(this.scale.max + 0.5);
        }
        ticks.forEach(function (tick) {
            var gridPosition = isCategroyAxis && padding ? _this.scale.scale(tick + 0.5) : _this.scale.scale(tick);
            var isLineOverlap = _this.parentModel.xAxis.some(function (axis) { return axis.getCrossPosition() === gridPosition && axis.option.axisLine.show; });
            if (isLineOverlap || !lodash.inRange(gridPosition, top - 0.5, bottom + 0.5)) {
                return;
            }
            var y = gridPosition;
            gridGroup.add(new Line({
                x1: left,
                y1: y,
                y2: y,
                x2: right,
                onEvent: function (event) { return _this.handleUIEvent(event, eventGraph.AXIS_GRID, {}); },
            }));
        });
        return gridGroup;
    };
    YAxis.prototype.renderLabels = function () {
        var _this = this;
        this.labelRef = {};
        var crossAt = this.state.crossAt;
        var _a = this.parentModel.getState().outerRect, left = _a.left, right = _a.right;
        var _b = this.option, opposite = _b.opposite, axisLabel = _b.axisLabel, offset = _b.offset;
        var inside = axisLabel.inside, margin = axisLabel.margin, rotate = axisLabel.rotate, offsetX = axisLabel.offsetX, offsetY = axisLabel.offsetY, optionTextAlign = axisLabel.textAlign;
        var textAlign = opposite === inside ? 'right' : 'left';
        var textBaseline = 'middle';
        var outerPosition = opposite ? right : left;
        var labelGroup = new Group({
            key: this.getKey('axis-label'),
        });
        var intervalTicks = this.getIntervalTicks();
        // allow user set textAlign when it is outer
        var alignItems = ((!opposite && optionTextAlign === 'left') || (opposite && optionTextAlign === 'right')) &&
            !inside;
        var labelX = alignItems ? outerPosition : crossAt;
        var labelAlign = alignItems ? optionTextAlign : textAlign;
        intervalTicks.forEach(function (tick, tickIndex) {
            var ref = createRef();
            _this.labelRef[tick] = ref;
            var text = _this.scale.getLabel(tick);
            var y = _this.scale.scale(tick);
            var x;
            if (opposite === inside) {
                x = labelX - margin;
            }
            else {
                x = labelX + margin;
            }
            if (alignItems) {
                x = outerPosition;
            }
            x += offset;
            labelGroup.add(createRichText(__assign(__assign({ ref: ref }, _this.getTextStyle(_this.option.axisLabel)), { textAlign: labelAlign, textBaseline: textBaseline,
                x: x,
                y: y,
                text: text, position: [offsetX, offsetY], origin: [x, y], rotation: degToRad(-rotate), onEvent: function (event) { return _this.handleUIEvent(event, eventGraph.AXIS_LABEL, {}); } })));
            if (intervalTicks.length < 8 && tickIndex === intervalTicks.length - 1) {
                for (var i = 0; i < 8 - intervalTicks.length; i++) {
                    labelGroup.add(createRichText({
                        opacity: 0,
                        fill: _this.option.axisLabel.color,
                        x: x + offset,
                        y: y - (_this.option.inverse ? -80 : 80),
                    }));
                }
            }
        });
        return labelGroup;
    };
    YAxis.prototype.renderTitle = function () {
        var _this = this;
        var _a = this.state, top = _a.top, bottom = _a.bottom, crossAt = _a.crossAt;
        var _b = this.option, opposite = _b.opposite, _c = _b.title, fontSize = _c.textStyle.fontSize, rotate = _c.rotate;
        var _d = this.option, _e = _d.title, text = _e.text, margin = _e.margin, align = _e.align, textStyle = _e.textStyle, offsetX = _e.offsetX, offsetY = _e.offsetY, offset = _d.offset;
        var x;
        var y;
        var textAlign;
        var textBaseline;
        if (align === 'start') {
            x = crossAt;
            y = this.scale.start + (this.option.inverse ? -1 : 1) * margin;
            textBaseline = this.option.inverse ? 'bottom' : 'top';
            textAlign = 'center';
        }
        else if (align === 'middle') {
            var labelSize = this.getLabelAreaSize().size;
            x = crossAt + (opposite ? 1 : -1) * (labelSize - fontSize / 2);
            y = (top + bottom) / 2;
            textBaseline = 'middle';
            textAlign = 'center';
        }
        else if (align === 'end') {
            x = crossAt;
            y = this.scale.end + (this.option.inverse ? 1 : -1) * margin;
            textBaseline = this.option.inverse ? 'top' : 'bottom';
            textAlign = 'center';
        }
        if (textStyle.textAlign) {
            textAlign = textStyle.textAlign;
        }
        if (textStyle.textBaseline) {
            textBaseline = textStyle.textBaseline;
        }
        x = x + offset + offsetX;
        y += offsetY;
        var titleRotation = lodash.isNumber(rotate)
            ? degToRad(rotate)
            : align === 'middle'
                ? degToRad(-90)
                : 0;
        return createRichText(__assign(__assign({ key: this.getKey('title'), x: x,
            y: y,
            text: text,
            textAlign: textAlign,
            textBaseline: textBaseline, origin: [x, y], rotation: titleRotation }, this.getTextStyle(this.option.title.textStyle)), { onEvent: function (event) { return _this.handleUIEvent(event, eventGraph.AXIS_TITLE, {}); } }));
    };
    YAxis.prototype.renderMarkLine = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('markline-y'),
        });
        this.option.markLines.forEach(function (markLine, index) {
            var value = markLine.value, label = markLine.label, lineStyle = markLine.lineStyle, icon = markLine.icon, range = markLine.range;
            var _a = _this.parentModel.getState(), top = _a.top, bottom = _a.bottom, gridLeft = _a.left, gridRight = _a.right;
            var offsetY = label.offsetY, offsetX = label.offsetX;
            var xAxisScale = _this.parentModel.xAxis[0].scale;
            var position = _this.scale.scale(value);
            var left = gridLeft;
            var right = gridRight;
            if (!inRange(position, [top, bottom])) {
                return;
            }
            if (isArray(range) && range.length > 0) {
                left = lodash.clamp(xAxisScale.scale(range[0]), gridLeft, gridRight);
                if (range[1]) {
                    right = lodash.clamp(xAxisScale.scale(range[1]), gridLeft, gridRight);
                }
            }
            var eventData = {
                axis: 'y',
                markLine: markLine,
                index: index,
                elments: {
                    label: createRef(),
                    icon: createRef(),
                    line: createRef(),
                },
            };
            var onMarkLineEvent = function (event) {
                return _this.handleUIEvent(event, eventGraph.MARKLINE, eventData);
            };
            var onMarkLineLabelEvent = function (event) {
                return _this.handleUIEvent(event, eventGraph.MARKLINE_LABEL, eventData);
            };
            var onMarklineIconEvent = function (event) {
                return _this.handleUIEvent(event, eventGraph.MARKLINE_ICON, eventData);
            };
            var x;
            var y = position;
            var textAlign;
            var textBaseline;
            if (label.position === 'start') {
                x = left - label.distance;
                textBaseline = 'middle';
                textAlign = 'right';
            }
            else if (label.position === 'middle') {
                x = (left + right) / 2;
                y = position - label.distance;
                textBaseline = 'bottom';
                textAlign = 'center';
            }
            else if (label.position === 'end') {
                x = right + label.distance;
                textBaseline = 'middle';
                textAlign = 'left';
            }
            var line = new Line(__assign(__assign({ ref: eventData.elments.line, x1: left, y1: position, x2: right, y2: position }, _this.getLineStyle(lineStyle)), { onEvent: onMarkLineEvent }));
            group.add(line);
            if (icon.symbol) {
                var symbolShape = getSymbolShapeFromCenter(right, position, icon.size);
                if (icon.symbol) {
                    var symbol = createSymbol(icon, symbolShape, __assign(__assign({ ref: eventData.elments.icon, display: icon.show }, _this.getShapeStyle(icon)), { onEvent: onMarklineIconEvent }));
                    group.add(symbol);
                }
            }
            var markLineLabel = createRichText(__assign(__assign({ ref: eventData.elments.label, display: label.show, text: label.text, x: x + offsetX, y: y + offsetY, textBaseline: textBaseline,
                textAlign: textAlign }, _this.getTextStyle(label)), { onEvent: onMarkLineLabelEvent }));
            group.add(markLineLabel);
        });
        return group;
    };
    YAxis.prototype.renderBreaks = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('breaks'),
        });
        var _a = this.parentModel.getState(), left = _a.left, right = _a.right;
        var gap = 5;
        var crossAt = this.state.crossAt;
        this.scale.getBreaks().forEach(function (breakItem) {
            var position = breakItem.position;
            // 水平线
            group.add(new Line({
                x1: left,
                x2: right,
                y1: position,
                y2: position,
                lineWidth: gap * 2,
                stroke: '#fff',
                pointerEvents: 'none',
            }));
            group.add(new Path(__assign(__assign({}, _this.getLineStyle(_this.option.axisLine)), { brush: function (ctx) {
                    ctx.moveTo(crossAt - gap, position + gap * 2);
                    ctx.lineTo(crossAt + gap, position);
                    ctx.moveTo(crossAt - gap, position);
                    ctx.lineTo(crossAt + gap, position - gap * 2);
                }, pointerEvents: 'none' })));
        });
        return group;
    };
    YAxis.prototype.render = function () {
        var _a;
        var group = new Group({
            key: this.getKey('axis-group'),
        });
        var hasBreaks = (_a = this.option.breaks) === null || _a === void 0 ? void 0 : _a.length;
        if (this.option.show) {
            if (this.option.alternateColor) {
                group.add(this.renderAlternateColor());
            }
            if (this.option.axisLine.show) {
                group.add(!hasBreaks ? this.renderAxisLine() : this.renderBrokenAxisLine());
            }
            if (this.getAxisSeries().length > 0 && this.option.gridLine.show) {
                group.add(this.renderGridLines());
            }
            if (this.option.tickLine.show) {
                group.add(this.renderTickLines());
            }
            if (this.option.axisLabel.show) {
                group.add(this.renderLabels());
            }
            if (this.option.title.show && this.option.title.text) {
                group.add(this.renderTitle());
            }
            group.add(this.renderCrossLabel());
            if (hasBreaks) {
                this.chart.addToTopLevel(this.renderBreaks());
            }
        }
        if (this.getAxisSeries().length > 0) {
            this.parentModel.addToGridTop(renderMarkArea(this));
            this.chart.addToTopLevel(this.renderMarkLine());
        }
        this.chart.addToTopLevel(this.crossLine);
        if (this.getCrosshairsOption().onTopLevel) {
            this.chart.addToTopLevel(this.crossShadow);
        }
        else {
            this.parentModel.addToGridTop(this.crossShadow);
        }
        return group;
    };
    return YAxis;
}(Axis));
export default YAxis;
